import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AiDashboard {
  modelId: number;
  modelVersionId: number;
  modelName: string;
  modelType: string;
  datasetId: number;
  datasetVersionId: number;
  datasetName: string;
  datasetVersion: number;
}

interface AiDetails {
  id: number;
  name: string;
  creator: string;
  dateCreated: string;
  organizationName: string;
}

interface Members {
  userRoles: {
    name: string;
    role: string;
  };
  total: number;
  completedTraining: number;
}

interface Reports {
  auditorApproved: number;
  total: number;
}

interface Controls {
  total: number;
  low: number;
  medium: number;
  high: number;
  other: number;
  list: {
    id: number;
    name: string;
    value: number | string;
    version: number | string;
    weight: number;
    influence: number;
    modelVersionId: number | string;
    datasetVersionId: number | string;
    uploadDate: string;
    creator: number | string;
  }[];
}

interface Models {
  ranExplainability: number;
  total: number;
}

interface PassWarningFail {
  pass: number;
  warning: number;
  fail: number;
  total: number;
}

interface Datasets {
  totalPass: number;
  totalWarning: number;
  totalFail: number;
  totalTests: number;
  total: number;
  riskAnalysisTools: {
    TotalSummary: PassWarningFail;
    BiasInspector: PassWarningFail;
    DataDrift: PassWarningFail;
    DataValidation: PassWarningFail;
  };
}

interface Explainability {
  [key: number]: {
    [key: string]: {
      name: string;
      value: number;
    }[];
  };
}

interface AiDashboardControls {
  id: number;
  tag_id: number;
  name: string;
  default: boolean;
  uploadDate: number;
  versionNumber: number | string;
  value: number | string;
  aiID: number;
  ai_model_id: number | string;
  ai_dataset_id: number | string;
  tag_value: number | string;
  uploadedBy: number | string;
  isSpecifications: boolean;
  specifications: string; // json
  riskRating: string;
  aiTagValVersions: {
    x: string;
    y: number;
    value: number;
    date: number;
    riskRating: string;
  };
}

export interface State {
  readonly members?: Members;
  readonly reports?: Reports;
  readonly controls?: Controls;
  readonly models?: Models;
  readonly datasets?: Datasets;
  readonly explainability?: Explainability;
  readonly modelDatasetCombos: AiDashboard[];
  readonly aiDetails?: AiDetails;
  readonly controlBundles: {
    name: string;
  }[];
  readonly riskScoreWeightedAverage?: string;
  readonly riskAnalysisSummary?: any;
  readonly controlsPanel?: AiDashboardControls[];
}

interface Payload {
  readonly members?: Members;
  readonly reports?: Reports;
  readonly controls?: Controls;
  readonly models?: Models;
  readonly datasets?: Datasets;
  readonly explainability?: Explainability;
  readonly modelDatasetCombos: AiDashboard[];
  readonly aiDetails?: AiDetails;
  readonly controlBundles: {
    name: string;
  }[];
  readonly riskScoreWeightedAverage?: string;
  readonly Explainability: any;
  readonly riskAnalysisSummary?: any;
  readonly controlsPanel?: AiDashboardControls[];
}

const initialState: State = {
  modelDatasetCombos: [],
  controlBundles: [],
  controlsPanel: [],
};

export const aiDashboardSlice = createSlice({
  name: 'aiDashboard',
  initialState,
  reducers: {
    setAiDashboard: (state, { payload }: PayloadAction<Payload>) => {
      state.members = payload.members;
      state.reports = payload.reports;
      state.controls = payload.controls;
      state.models = payload.models;
      state.datasets = payload.datasets;
      state.explainability = payload.explainability;
      state.modelDatasetCombos = payload.modelDatasetCombos;
      state.controlBundles = payload.controlBundles;
      state.riskScoreWeightedAverage = payload.riskScoreWeightedAverage;
      state.riskAnalysisSummary = payload.riskAnalysisSummary;
    },

    setAiDetails: (
      state,
      { payload }: PayloadAction<AiDetails | undefined>
    ) => {
      state.aiDetails = payload;
    },

    setAiDashboardControls: (
      state,
      { payload }: PayloadAction<AiDashboardControls[] | undefined>
    ) => {
      state.controlsPanel = payload;
    },
  },
});
